/**
 * Chart rendering for document exports (MOR-978).
 *
 * The Solvency Analysis worksheet shows charts alongside its tables, and the
 * practitioner attaches the export to creditors' reports — so the Word/PDF
 * exports must carry the same graphs, not just the numbers.
 *
 * Split in two so it stays testable:
 *   - `buildChartSvg` is a pure function from a chart spec to an SVG string.
 *     It mirrors the on-screen Recharts components (grouped/stacked bars,
 *     overlay lines, the 1.0 solvency threshold) using the light-theme chart
 *     palette, and runs anywhere — unit tests exercise it under node.
 *   - `renderChartPng` rasterises that SVG via an offscreen canvas for
 *     embedding in docx/pdf-lib. Browser-only; returns null when no canvas is
 *     available so exports degrade to tables instead of failing.
 */

export interface ExportChartSeries {
  label: string;
  kind: "bar" | "stacked-bar" | "line";
  /** Fill/stroke colour as a hex string (print exports are light-theme). */
  color: string;
  /** Bars only: fill for negative values (defaults to `color`). */
  negativeColor?: string;
  /** One value per chart label; null renders as 0, matching the UI charts. */
  values: (number | null)[];
}

export interface ExportChart {
  /** Drawn as a small heading above the image in Word/PDF. */
  title: string;
  /** X-axis period labels. */
  labels: string[];
  series: ExportChartSeries[];
  valueFormat?: "currency" | "ratio";
  /** Dashed horizontal rule, e.g. the 1.0 current-ratio threshold. */
  referenceLineAt?: number;
}

/** Light-theme chart palette (--chart-1..5 in src/index.css, as hex). */
export const EXPORT_CHART_COLORS = {
  chart1: "#e76e50",
  chart2: "#2a9d90",
  chart3: "#274754",
  chart4: "#e8c468",
  chart5: "#f4a462",
} as const;

export const CHART_WIDTH = 640;
export const CHART_HEIGHT = 260;

const MARGIN = { top: 14, right: 16, bottom: 52, left: 64 };
const AXIS_TEXT = "#6b7280";
const GRID = "#e5e7eb";
const ZERO_AXIS = "#9ca3af";
const FONT = "Helvetica, Arial, sans-serif";
const TICK_FONT_SIZE = 10;
const LEGEND_FONT_SIZE = 10;

function esc(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function formatTick(v: number, format: ExportChart["valueFormat"]): string {
  if (format === "ratio") {
    if (Math.abs(v) >= 10) return v.toFixed(0);
    return Number.isInteger(v * 10) ? v.toFixed(1) : v.toFixed(2);
  }
  // Same $Xk convention as the on-screen charts' axis formatter.
  const thousands = v / 1000;
  const label =
    Math.abs(thousands) >= 100 || Number.isInteger(thousands)
      ? thousands.toFixed(0)
      : Math.abs(thousands) >= 1
        ? thousands.toFixed(0)
        : thousands.toFixed(1);
  return `$${label}k`;
}

/** Largest "nice" step (1/2/2.5/5 × 10^n) that yields ≥3 ticks over `range`. */
function niceStep(range: number): number {
  const rough = range / 4;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rough)));
  for (const m of [5, 2.5, 2, 1]) {
    if (m * magnitude <= rough) return m * magnitude;
  }
  return magnitude;
}

interface Domain {
  min: number;
  max: number;
  ticks: number[];
}

/**
 * Y domain across every series, always including zero (bars need a
 * baseline) and any reference line. Stacked series contribute their
 * positive and negative sums per column, mirroring Recharts' stacking.
 */
interface ColumnStack {
  positive: number;
  negative: number;
  min: number;
  max: number;
}

/** Per-column stacked-bar sums plus the min/max of every other series' value. */
function computeColumnStack(chart: ExportChart, i: number): ColumnStack {
  return chart.series.reduce<ColumnStack>(
    (acc, s) => {
      const v = s.values[i] ?? 0;
      if (s.kind === "stacked-bar") {
        return v >= 0
          ? { ...acc, positive: acc.positive + v }
          : { ...acc, negative: acc.negative + v };
      }
      return { ...acc, max: Math.max(acc.max, v), min: Math.min(acc.min, v) };
    },
    { positive: 0, negative: 0, min: 0, max: 0 },
  );
}

function computeDomain(chart: ExportChart): Domain {
  const n = chart.labels.length;
  const columns = Array.from({ length: n }, (_, i) =>
    computeColumnStack(chart, i),
  );

  const seriesMin = Math.min(
    0,
    ...columns.map((c) => Math.min(c.min, c.negative)),
  );
  const seriesMax = Math.max(
    0,
    ...columns.map((c) => Math.max(c.max, c.positive)),
  );

  const min =
    chart.referenceLineAt != null
      ? Math.min(seriesMin, chart.referenceLineAt)
      : seriesMin;
  const maxWithRef =
    chart.referenceLineAt != null
      ? Math.max(seriesMax, chart.referenceLineAt)
      : seriesMax;
  const max = min === 0 && maxWithRef === 0 ? 1 : maxWithRef;

  const step = niceStep(max - min);
  const lo = Math.floor(min / step) * step;
  const hi = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  // Guard against FP drift accumulating past `hi` or dirtying tick labels.
  for (let t = lo; t <= hi + step / 2; t += step) {
    ticks.push(Math.abs(t) < step / 1e6 ? 0 : Number(t.toFixed(9)));
  }
  return { min: lo, max: hi, ticks };
}

/**
 * Build a self-contained SVG for a chart spec. Pure — no DOM required.
 */
export function buildChartSvg(
  chart: ExportChart,
  width: number = CHART_WIDTH,
  height: number = CHART_HEIGHT,
): string {
  const plotW = width - MARGIN.left - MARGIN.right;
  const plotH = height - MARGIN.top - MARGIN.bottom;
  const n = Math.max(chart.labels.length, 1);
  const domain = computeDomain(chart);

  const yFor = (v: number) =>
    MARGIN.top + plotH - ((v - domain.min) / (domain.max - domain.min)) * plotH;
  const bandW = plotW / n;
  const bandCenter = (i: number) => MARGIN.left + bandW * (i + 0.5);

  const parts: string[] = [];
  parts.push(
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" font-family="${FONT}">`,
    `<rect x="0" y="0" width="${width}" height="${height}" fill="#ffffff"/>`,
  );

  // Horizontal grid + y tick labels.
  for (const t of domain.ticks) {
    const y = yFor(t);
    parts.push(
      `<line x1="${MARGIN.left}" y1="${y.toFixed(1)}" x2="${width - MARGIN.right}" y2="${y.toFixed(1)}" stroke="${t === 0 ? ZERO_AXIS : GRID}" stroke-width="1"/>`,
      `<text x="${MARGIN.left - 8}" y="${(y + 3).toFixed(1)}" text-anchor="end" font-size="${TICK_FONT_SIZE}" fill="${AXIS_TEXT}">${esc(formatTick(t, chart.valueFormat))}</text>`,
    );
  }

  // Bars — grouped side by side; all stacked series share one group slot.
  const barSeries = chart.series.filter((s) => s.kind === "bar");
  const stackedSeries = chart.series.filter((s) => s.kind === "stacked-bar");
  const groupSlots = barSeries.length + (stackedSeries.length > 0 ? 1 : 0);
  const zeroY = yFor(Math.max(domain.min, Math.min(0, domain.max)));

  if (groupSlots > 0) {
    const groupW = bandW * 0.62;
    const barW = Math.max(groupW / groupSlots, 2);
    for (let i = 0; i < chart.labels.length; i++) {
      const groupX = bandCenter(i) - groupW / 2;

      barSeries.forEach((s, slot) => {
        const v = s.values[i] ?? 0;
        if (v === 0) return;
        const x = groupX + slot * barW;
        const y = yFor(Math.max(v, 0));
        const h = Math.abs(y - yFor(Math.min(v, 0)));
        const fill = v < 0 ? (s.negativeColor ?? s.color) : s.color;
        parts.push(
          `<rect x="${x.toFixed(1)}" y="${Math.min(y, zeroY).toFixed(1)}" width="${(barW * 0.9).toFixed(1)}" height="${h.toFixed(1)}" rx="1.5" fill="${fill}"/>`,
        );
      });

      if (stackedSeries.length > 0) {
        const x = groupX + barSeries.length * barW;
        let posBase = 0;
        let negBase = 0;
        for (const s of stackedSeries) {
          const v = s.values[i] ?? 0;
          if (v === 0) continue;
          const from = v >= 0 ? posBase : negBase;
          const to = from + v;
          const yTop = yFor(Math.max(from, to));
          const h = Math.abs(yFor(from) - yFor(to));
          parts.push(
            `<rect x="${x.toFixed(1)}" y="${yTop.toFixed(1)}" width="${(barW * 0.9).toFixed(1)}" height="${h.toFixed(1)}" fill="${s.color}"/>`,
          );
          if (v >= 0) posBase = to;
          else negBase = to;
        }
      }
    }
  }

  // Reference line (dashed) above bars, below lines.
  if (chart.referenceLineAt != null) {
    const y = yFor(chart.referenceLineAt);
    parts.push(
      `<line x1="${MARGIN.left}" y1="${y.toFixed(1)}" x2="${width - MARGIN.right}" y2="${y.toFixed(1)}" stroke="${AXIS_TEXT}" stroke-width="1" stroke-dasharray="4 3"/>`,
    );
  }

  // Lines with dots, drawn over the bars like the on-screen overlays.
  for (const s of chart.series) {
    if (s.kind !== "line") continue;
    const pts = chart.labels.map((_, i) => {
      const v = s.values[i] ?? 0;
      return `${bandCenter(i).toFixed(1)},${yFor(v).toFixed(1)}`;
    });
    parts.push(
      `<polyline points="${pts.join(" ")}" fill="none" stroke="${s.color}" stroke-width="2" stroke-linejoin="round"/>`,
    );
    chart.labels.forEach((_, i) => {
      const v = s.values[i] ?? 0;
      parts.push(
        `<circle cx="${bandCenter(i).toFixed(1)}" cy="${yFor(v).toFixed(1)}" r="3" fill="${s.color}" stroke="#ffffff" stroke-width="1.5"/>`,
      );
    });
  }

  // X labels — skip every k-th when they would collide.
  const maxLabelChars = Math.max(...chart.labels.map((l) => l.length), 1);
  const approxLabelW = maxLabelChars * (TICK_FONT_SIZE * 0.58) + 6;
  const skip = Math.max(1, Math.ceil((approxLabelW * n) / plotW));
  chart.labels.forEach((label, i) => {
    if (i % skip !== 0 && i !== chart.labels.length - 1) return;
    // Drop a colliding final label rather than overlapping its neighbour.
    if (
      i === chart.labels.length - 1 &&
      i % skip !== 0 &&
      bandCenter(i) - bandCenter(i - (i % skip)) < approxLabelW
    ) {
      return;
    }
    parts.push(
      `<text x="${bandCenter(i).toFixed(1)}" y="${MARGIN.top + plotH + 16}" text-anchor="middle" font-size="${TICK_FONT_SIZE}" fill="${AXIS_TEXT}">${esc(label)}</text>`,
    );
  });

  // Legend — centred swatch row along the bottom.
  const legendItems = chart.series.map((s) => s.label);
  const itemWidths = legendItems.map(
    (l) => 14 + l.length * (LEGEND_FONT_SIZE * 0.58) + 14,
  );
  const legendW = itemWidths.reduce((a, b) => a + b, 0);
  const legendStart = Math.max((width - legendW) / 2, MARGIN.left);
  const legendX = itemWidths.reduce<{ x: number; positions: number[] }>(
    (acc, w) => ({ x: acc.x + w, positions: [...acc.positions, acc.x] }),
    { x: legendStart, positions: [] },
  ).positions;
  const ly = height - 12;
  chart.series.forEach((s, idx) => {
    const lx = legendX[idx];
    parts.push(
      `<rect x="${lx.toFixed(1)}" y="${ly - 8}" width="9" height="9" rx="2" fill="${s.color}"/>`,
      `<text x="${(lx + 13).toFixed(1)}" y="${ly}" font-size="${LEGEND_FONT_SIZE}" fill="#374151">${esc(s.label)}</text>`,
    );
  });

  parts.push("</svg>");
  return parts.join("");
}

export interface RenderedChart {
  /** PNG bytes ready for docx ImageRun / pdf-lib embedPng. */
  bytes: Uint8Array;
  /** Layout size in CSS px (the bitmap itself is `scale`× larger). */
  width: number;
  height: number;
}

/**
 * Rasterise a chart to PNG in the browser. Returns null in environments
 * without DOM/canvas (tests, SSR) or if rasterisation fails for any reason —
 * callers embed nothing rather than failing the whole export.
 */
export async function renderChartPng(
  chart: ExportChart,
  options: { width?: number; height?: number; scale?: number } = {},
): Promise<RenderedChart | null> {
  const width = options.width ?? CHART_WIDTH;
  const height = options.height ?? CHART_HEIGHT;
  const scale = options.scale ?? 2;

  if (typeof document === "undefined" || typeof Image === "undefined") {
    return null;
  }

  try {
    const svg = buildChartSvg(chart, width, height);
    const url = URL.createObjectURL(
      new Blob([svg], { type: "image/svg+xml;charset=utf-8" }),
    );
    try {
      const img = new Image();
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error("SVG chart failed to load"));
        img.src = url;
      });

      const canvas = document.createElement("canvas");
      canvas.width = width * scale;
      canvas.height = height * scale;
      const ctx = canvas.getContext("2d");
      if (!ctx) return null;
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      const dataUrl = canvas.toDataURL("image/png");
      const base64 = dataUrl.split(",")[1];
      if (!base64) return null;
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      return { bytes, width, height };
    } finally {
      URL.revokeObjectURL(url);
    }
  } catch (err) {
    console.error(
      "Chart rasterisation failed; exporting without this chart",
      err,
    );
    return null;
  }
}

/** Render every chart on a sheet, dropping any that could not rasterise. */
export async function renderChartsForExport(
  charts: ExportChart[] | undefined,
): Promise<Array<{ chart: ExportChart; image: RenderedChart }>> {
  if (!charts || charts.length === 0) return [];
  const rendered: Array<{ chart: ExportChart; image: RenderedChart }> = [];
  for (const chart of charts) {
    const image = await renderChartPng(chart);
    if (image) rendered.push({ chart, image });
  }
  return rendered;
}
